'use client'

import { useQuery } from '@apollo/client'
import {
  WATCH_PARTY_RESET_PARAM,
  clearWatchPartyDismissedKey,
  findWatchParty,
  getWatchPartyDismissedKey,
  setWatchPartyDismissedKey,
  type WatchPartySession,
} from 'app/lib/watchParty'
import { useSearchParams } from 'next/navigation'
import { useEffect, useState, useSyncExternalStore } from 'react'
import { gql } from '~/__generated__'
import { useMyLanguage } from './useMyLanguage'

const GET_WATCH_PARTY_PROMPT_QUERY = gql(`
  query getWatchPartyPrompt($id: ID!) {
    comment(id: $id) {
      id
      videoId
      userId
      contents
      isMine
      user {
        id
        atId
        name
        avatar
        createdAt
        updatedAt
      }
      createdAt
      replies {
        totalCount
      }
      currentUserHasLike
      likeNum
      isSpoiler
      commentTranslations {
        id
        contents
        locale
      }
    }
  }
`)

const dismissStore = {
  key: undefined as string | null | undefined,
  listeners: new Set<() => void>(),
  subscribe: (listener: () => void) => {
    dismissStore.listeners.add(listener)
    return () => {
      dismissStore.listeners.delete(listener)
    }
  },
  get: () => {
    if (dismissStore.key === undefined) {
      dismissStore.key = getWatchPartyDismissedKey()
    }
    return dismissStore.key
  },
  set: (key: string | null) => {
    if (dismissStore.key === key) {
      return
    }
    if (key === null) {
      clearWatchPartyDismissedKey()
    } else {
      setWatchPartyDismissedKey(key)
    }
    dismissStore.key = key
    dismissStore.listeners.forEach((listener) => listener())
  },
}

export const useWatchPartyDismiss = () => {
  const dismissedKey = useSyncExternalStore(
    dismissStore.subscribe,
    dismissStore.get,
    () => null as string | null,
  )

  return {
    dismiss: (key: string) => dismissStore.set(key),
    reset: () => dismissStore.set(null),
    isDismissed: (key: string) => dismissedKey === key,
  }
}

export const useWatchPartyDismissReset = () => {
  const { reset } = useWatchPartyDismiss()
  const active = useSearchParams()?.get(WATCH_PARTY_RESET_PARAM) === '1'

  useEffect(() => {
    if (active) {
      reset()
    }
  }, [active])
}

export const useWatchParty = (videoId?: string): WatchPartySession | null => {
  const language = useMyLanguage()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted || language !== 'ja') {
    return null
  }

  return findWatchParty(Date.now(), videoId)
}

export const useWatchPartyPrompt = (commentId: string | undefined) => {
  const { data, loading } = useQuery(GET_WATCH_PARTY_PROMPT_QUERY, {
    variables: { id: commentId! },
    skip: !commentId,
  })

  return { prompt: data?.comment ?? null, loading }
}
